import {
  Component,
  HostListener,
  Input,
  QueryList,
  ViewChild,
  ViewChildren,
  OnInit,
  EventEmitter,
  Output,
  type SimpleChanges,
  OnChanges
} from '@angular/core';
import { NgbInputDatepicker, NgbTypeahead, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, BehaviorSubject, of, merge } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap
} from 'rxjs/operators';
import {
  ServiceType,
  EnterpriseSearchService,
  HelperRoutines,
  UserService,
  ModalOpenerService,
  CabHireVehicleType,
  WithSubscriptionComponent,
  GetGeoLocationResult,
  TransportType
} from '@sabstravtech/obtservices/angular';
import {
  AddressForm,
  RailAirport,
  CabHireEnterpriseSearchInterface,
  TaxiFormMode,
  OBTAirlinesDetails,
  LocationDetails,
  LocationTypes,
  GeoLocationResult,
  TransType
} from '@sabstravtech/obtservices/base';
import moment from 'moment';

import { UiHelpers } from '../../../core/classes/ui-helpers';
import { LightningModalTypes } from '../../../core/classes/modal-types.enum';
import { Helpers } from '../../../core/classes/helpers';

@Component({
  selector: 'app-taxi-multi-location-form',
  templateUrl: './taxi-multi-location-form.component.html',
  styleUrls: ['./taxi-multi-location-form.component.scss'],
  standalone: false
})
export class TaxiMultiLocationFormComponent
  extends WithSubscriptionComponent
  implements OnInit, OnChanges
{
  @ViewChildren(NgbInputDatepicker) datepickerList: QueryList<NgbInputDatepicker>;
  @ViewChild('inputAddress') inputAddress: NgbTypeahead;
  @ViewChild('inputStation') inputStation: NgbTypeahead;

  @ViewChild('inputHouse') inputHouse: NgbTypeahead;
  @ViewChild('inputStreet') inputStreet: NgbTypeahead;
  @ViewChild('inputTown') inputTown: NgbTypeahead;
  @ViewChild('inputPostCode') inputPostCode: NgbTypeahead;

  @Input() title: string;
  @Input() legendTitle: string;
  @Input() modeType: TaxiFormMode = TaxiFormMode.ADDRESS;
  @Input() required: boolean;
  @Input() departure: boolean;
  @Input() address: AddressForm;
  @Input() limo = false;
  @Input() isCmacOnly = false;
  @Input() isVia = false;
  @Input() isStopChecked = false;
  @Input() showDateBlock = false;
  @Input() hasGroundspan = false;
  @Input() transTypes: TransType[] = [];
  @Input() limoDurations: number[] = [];
  @Output() addressChanged: EventEmitter<AddressForm> = new EventEmitter<AddressForm>();
  @Input() station: RailAirport;
  @Output() stationChange: EventEmitter<RailAirport> = new EventEmitter<RailAirport>();
  @Output() locationChange: EventEmitter<GeoLocationResult> = new EventEmitter<GeoLocationResult>();
  @Output() addStopChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  CabHireVehicleType = CabHireVehicleType;
  TransportType = TransportType;
  formatter_airlines = (x: OBTAirlinesDetails) => x.name;
  public addressForm: AddressForm = {
    House: '',
    Street: '',
    Town: '',
    PostCode: '',
    Country: '',
    placeDescription: '',
    FlightNumber: '',
    Carrier: '',
    FlightTrainDateTime: '',
    Name: ''
  };
  timesArray: string[];

  carrier: string | OBTAirlinesDetails;

  TaxiFormMode: typeof TaxiFormMode = TaxiFormMode;

  mode: TaxiFormMode = TaxiFormMode.ADDRESS; // Defaults should be address

  addressClick$: Subject<string> = new Subject();
  addressList: Observable<AddressForm[]>;

  stationFocus$: Subject<string> = new Subject();
  airlineFocus$: Subject<string> = new Subject();

  typeaHeadLimit = Number.MAX_SAFE_INTEGER;

  reloadAddresses: boolean;
  savingAddress = false;
  isGRS = false;
  loadingAddresses: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loadingStations: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  arriveLocationIsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  LocationTypes: typeof LocationTypes = LocationTypes;
  invalidAirlineSelected = false;
  streetMissing = false;
  // Controls visibility of the manual house/street inputs. Keep this independent from
  // per-keystroke completeness checks so the inputs do not collapse while the user is typing.
  showManualStreetFields = false;
  formatAddresses = (address: AddressForm): string => {
    const { Name, House, Street, Country, Town, PostCode } = address;
    if (!House && !Street && !Town && !PostCode && !Name) {
      return;
    }

    const details = [Town, PostCode].filter(Boolean).join(', ');

    if (Name) {
      if (!details && !Country) {
        return Name;
      }
      return `${Name}${details ? `, ${Country}, ${details}` : ''}`;
    }

    const parts = [House, Street, Town, Country, PostCode].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '';
  };

  formatStations = (railAirport: any) => railAirport.name;

  searchParams: CabHireEnterpriseSearchInterface;
  type: LocationTypes;
  airportInfo: any;
  limosCannotBeBooked = false;
  transformedTitle: string;
  today: NgbDateStruct = Helpers.dateToNgDate(new Date());

  get validationType(): string {
    return this.showDateBlock ? 'From' : this.isVia ? 'Via To' : 'To';
  }

  get hasLocationError(): boolean {
    if (!this.searchService.show_errors) return false;
    const prefix = `${this.validationType} - Enter a location or postcode`;
    return !!this.searchParams?.validationMessages?.some((msg) => msg.startsWith(prefix));
  }

  get hasHouseError(): boolean {
    if (!this.searchService.show_errors) return false;
    if (this.address?.House) return false;
    const prefix = `${this.validationType} - Enter house number/name and street`;
    return !!this.searchParams?.validationMessages?.some((msg) => msg.startsWith(prefix));
  }

  get hasStreetError(): boolean {
    if (!this.searchService.show_errors) return false;
    if (this.address?.Street) return false;
    const prefix = `${this.validationType} - Enter house number/name and street`;
    return !!this.searchParams?.validationMessages?.some((msg) => msg.startsWith(prefix));
  }
  constructor(
    public searchService: EnterpriseSearchService,
    private helpers: HelperRoutines,
    private userService: UserService,
    private modalOpenerService: ModalOpenerService
  ) {
    super();
    this.searchParams = searchService.searches[ServiceType.Cab];
  }

  ngOnInit() {
    if (!this.address) {
      this.address = Object.assign({}, this.addressForm);
    }
    if (this.address?.House) {
      this.type =
        this.address?.House === LocationTypes.TrainStation
          ? LocationTypes.TrainStation
          : this.address?.House === LocationTypes.Airport
            ? LocationTypes.Airport
            : LocationTypes.Address;
    }
    if (this.address?.Carrier) {
      this.loadCarrier();
    }

    this.mode = this.modeType;
    this.timesArray = Helpers.generateTimesList(0, 24);
    // ENT-9207 set to GB
    this.setDefaultCountryCode();
    this.transformedTitle = this.title.replace(/\s+/g, '-');
    // this.isGRS = this.userService.getServices('CTX')[0].provider === 'GRS';
    const terminals = this.showDateBlock
      ? this.searchParams.fromTerminals
      : this.isVia
        ? this.searchParams.viaTerminals
        : this.searchParams.toTerminals;
    if (!terminals?.length && this.address?.Terminal) {
      const newTerminals = [
        {
          key: this.address.Terminal,
          value: this.address.Terminal
        }
      ];
      if (this.showDateBlock) {
        this.searchParams.fromTerminals = newTerminals;
      } else if (this.isVia) {
        this.searchParams.viaTerminals = newTerminals;
      } else {
        this.searchParams.toTerminals = newTerminals;
      }
    }
  }

  getTimeArray(inputTime: string, afterInput: boolean, currentValue?: string): string[] {
    const inputParts = inputTime.split(':');
    const hours = parseInt(inputParts[0], 10);
    const minutes = parseInt(inputParts[1], 10);
    const timeArray: string[] = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        if (
          (afterInput && (h > hours || (h === hours && m > minutes))) ||
          (!afterInput && (h < hours || (h === hours && m < minutes)))
        ) {
          timeArray.push(moment({ h, m }).format('HH:mm'));
        }
      }
    }
    // Always include the current value so chatbot-set times are selectable
    if (currentValue && !timeArray.includes(currentValue)) {
      timeArray.unshift(currentValue);
    }
    return timeArray;
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    UiHelpers.closeOpenCalendars(this.datepickerList, event);
  }

  getAddresses = (): Observable<unknown> => {
    return this.addressClick$.pipe(
      switchMap(
        () => (this.addressList = this.searchService.getUserAddresses(this.loadingAddresses))
      )
    );
  };

  updateStation(event: MouseEvent | FocusEvent) {
    this.stationFocus$.next((<HTMLInputElement>event.target).value);
  }

  updateAirline(event: MouseEvent | FocusEvent) {
    this.airlineFocus$.next((<HTMLInputElement>event.target).value);
  }

  updateAddress(event: MouseEvent | FocusEvent) {
    this.addressClick$.next((<HTMLInputElement>event.target).value);
  }

  getGeoLocations = (text$: Observable<string>) => {
    return text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term: string): Observable<LocationDetails[]> =>
        term.length <= 2
          ? of([])
          : this.searchService
              .getGeoLocations(term, this.isCmacOnly ? 'GB' : null, this.loadingStations)
              .pipe(
                map((res: any[]) => {
                  // show railstation and airports with postcode only
                  // return res.filter(re => re.railstation?.locationDetails?.postcode || re.airport?.postcode).slice(0, this.typeaHeadLimit);
                  return res;
                }),
                catchError(() => of([]))
              )
      )
    );
  };

  getAirline = (text$: Observable<string>): Observable<OBTAirlinesDetails[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap((term: string): Observable<OBTAirlinesDetails[]> =>
        term.length <= 2
          ? of([])
          : this.searchService.getAirlinesList(term, this.arriveLocationIsLoading).pipe(
              map((res: any[]) => {
                console.log(res);
                return res.slice(0, this.typeaHeadLimit);
              }),
              catchError(() => of([]))
            )
      )
    );

  getList(list: any) {
    if (list.type === LocationTypes.Airport) {
      return `${list.name} (${list.airport.gateway})`;
    }
    return list.name;
  }

  saveAddress(): void {
    this.savingAddress = true;
    this.searchService
      .saveUserAddress(this.address)
      .then((data) => {
        console.log(data);

        if (data.alreadySaved) {
          this.modalOpenerService.open(LightningModalTypes.TaxiWarningModalComponent);
        }
      })
      .finally(() => (this.savingAddress = false));
  }

  clearAddress(): void {
    const station = this.address?.Terminal || this.address?.Terminal === null;
    this.type = undefined;
    this.airportInfo = null;
    (station ? this.stationChange.emit(this.airportInfo) : this.addressChanged.emit(),
      this.stationChange.emit());
    this.address = {
      ...this.addressForm,
      Country: !this.limo ? 'GB' : ''
    };
    if (this.showDateBlock) {
      this.searchParams.fromTerminals = [];
    } else {
      this.searchParams.toTerminals = [];
    }
    this.streetMissing = false;
    this.showManualStreetFields = false;
    this.onChange();
  }

  deleteAddress(address: AddressForm, event: any): void {
    const currentEvent = event.currentTarget?.parentNode?.parentNode;
    event.stopPropagation();
    this.subscribe(this.searchService.deleteUserAddressByID(address.ID.toString()), (response) => {
      if (response) {
        currentEvent.remove();
      }
    });
  }

  ensureElementIsScrolledTo(event) {
    try {
      const typeAheadList = event.target.nextElementSibling;
      const activeButton = typeAheadList.getElementsByClassName('active')[0];
      if (
        activeButton.offsetTop + activeButton.clientHeight >
        typeAheadList.clientHeight + typeAheadList.scrollTop
      ) {
        typeAheadList.scrollTop =
          activeButton.offsetTop + activeButton.clientHeight - typeAheadList.clientHeight;
      } else if (activeButton.offsetTop < typeAheadList.scrollTop) {
        typeAheadList.scrollTop = activeButton.offsetTop;
      }
    } catch (e) {
      console.log("Couldn't find elements to scroll");
    }
  }

  selectedAddress(value: any) {
    this.stationChange.emit();
    this.addressChanged.emit(value.item);
    this.updateStreetMissing(value.item, false);
  }

  selectedStation(value: RailAirport) {
    this.stationChange.emit(value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.address) {
      this.address = Object.assign({}, this.addressForm);
    }

    if (changes.limo) {
      this.setDefaultCountryCode();
    }

    if (changes.address && this.address?.House) {
      this.type =
        this.address.House === LocationTypes.TrainStation
          ? LocationTypes.TrainStation
          : this.address.House === LocationTypes.Airport
            ? LocationTypes.Airport
            : LocationTypes.Address;

      // Re-load airline when address changes (e.g. populated by chatbot)
      this.loadCarrier();
    }
  }

  // set country if no postcode or country code, otherwise retain previous value from the search
  setDefaultCountryCode() {
    if (this.address.PostCode === '' || this.address.Country === '') {
      this.address.Country = !this.limo ? 'GB' : '';
    }
  }

  private loadCarrier(): void {
    if (!this.address?.Carrier) return;
    if (this.type === LocationTypes.Airport) {
      this.subscribe(
        this.searchService.getAirlinesList(this.address.Carrier, this.arriveLocationIsLoading),
        (airlines: OBTAirlinesDetails[]) => {
          const match = airlines.find((a) => a.code === this.address.Carrier);
          this.carrier = match || { code: this.address.Carrier, name: this.address.Carrier };
        }
      );
    } else {
      this.carrier = this.address.Carrier;
    }
  }

  resultFormatBandListValue(address: any) {
    return `${address.House} ${address.Street || ''}, ${address.PostCode}`;
  }

  /**
   * @description - returns boolean for whether the address is valid or not, true if house and postcode provided
   */
  validAddress(): boolean {
    return !!(
      this.address?.House ||
      this.address?.Street ||
      this.address?.Town ||
      (this.address?.PostCode && this.address?.PostCode?.length >= 3)
    );
  }

  onChange() {
    this.invalidAirlineSelected = false;
    if (this.type === LocationTypes.Airport) {
      this.validateAirlineSelection();
      this.address.Carrier = (this.carrier as OBTAirlinesDetails)?.code || '';
    } else if (this.type === LocationTypes.TrainStation) {
      this.address.Carrier = this.carrier as string;
    }
    this.address.House?.replace(/\s/g, '');
    this.address.PostCode?.replace(/\s/g, '');
    this.address.Street?.replace(/\s/g, '');
    this.address.Town?.replace(/\s/g, '');
    this.address.Country?.replace(/\s/g, '');
    this.address.FlightTrainDateTime?.replace(/\s/g, '');
    this.addressChanged.emit(this.address);
    if (this.limo) {
      this.canLimosBeBooked();
    }
  }

  canLimosBeBooked() {
    this.limosCannotBeBooked = this.searchService.countriesWithoutPostalCodes.some(
      (item) => item.cCode === this.address.Country
    );
  }

  validateAirlineSelection() {
    this.invalidAirlineSelected = (this.carrier as OBTAirlinesDetails)?.code ? false : true;
  }

  openRemoveAddressesModal() {
    this.modalOpenerService.open(
      LightningModalTypes.ModalDeleteSavedAddresses,
      { centered: true },
      {
        addresses: this.searchService.getUserAddresses(this.loadingAddresses),
        removeAddress: this.deleteAddress,
        searchService: this.searchService
      }
    );
  }

  onInputClear(value: string): void {
    if (!value || value.trim() === '') {
      this.clearAddress();
    }
  }

  formatLocationAddress(r: any): string {
    const name = r?.name || r?.Name;
    const house = r?.house || r?.House;
    const street = r?.street || r?.Street;
    const city = r?.city || r?.Town;
    const postcode = r?.postcode || r?.PostCode;
    const country = r?.countryCode || r?.Country;

    if (name) {
      const nameHasDetails = (city && name.includes(city)) || (postcode && name.includes(postcode));
      if (nameHasDetails) {
        return name;
      }
      const details = [city, postcode].filter(Boolean).join(', ');
      if (!details && !country) {
        return name;
      }
      return `${name}${details ? `, ${country} ${details}` : ''}`;
    }

    const location = [house, street, city, postcode].filter(Boolean).join(', ');
    return location;
  }

  setLocation(location: GeoLocationResult) {
    console.log(location);
    if (!this.address) {
      this.address = Object.assign({}, this.addressForm);
    }
    this.type = location.type as LocationTypes;
    this.carrier = '';
    if (location.type === LocationTypes.Airport) {
      this.airportInfo = location;
      // Store terminals in searchParams based on location type
      if (this.showDateBlock) {
        this.searchParams.fromTerminals = location.terminals;
      } else if (this.isVia) {
        this.searchParams.viaTerminals = location.terminals;
      } else {
        this.searchParams.toTerminals = location.terminals;
      }
      this.addTerminal(location.terminals[0].key, true);
    }
    this.updateStreetMissing(location, false);
    this.locationChange.emit(location);
  }

  // House/Street (streetAndNumber) is required by the taxi provider. Airport/Train Station
  // results have their own dedicated fields, so they're excluded from this check.
  private updateStreetMissing(location: any, preserveManualInputVisibility = true): void {
    const type = location?.type;
    if (type === LocationTypes.Airport || type === LocationTypes.TrainStation) {
      this.streetMissing = false;
      this.showManualStreetFields = false;
      return;
    }
    const house = location?.house ?? location?.House;
    const street = location?.street ?? location?.Street;
    this.streetMissing = !house || !street;

    // On explicit location selection/update, sync visibility with the selected location.
    // During typing in manual inputs, keep the block open even when both fields become non-empty.
    if (!preserveManualInputVisibility) {
      this.showManualStreetFields = this.streetMissing;
    } else if (this.streetMissing) {
      this.showManualStreetFields = true;
    }
  }

  onHouseInputChange(value: string): void {
    this.address.House = value;
    this.streetMissing = !this.address.House || !this.address.Street;
    // Do not auto-hide while typing; hide only on explicit context change/reset.
    this.showManualStreetFields = true;
    this.onChange();
  }

  onStreetInputChange(value: string): void {
    this.address.Street = value;
    this.streetMissing = !this.address.House || !this.address.Street;
    // Do not auto-hide while typing; hide only on explicit context change/reset.
    this.showManualStreetFields = true;
    this.onChange();
  }

  addTerminal(value: string, isSetLocation = false) {
    if (this.airportInfo?.terminals?.length) {
      this.airportInfo.terminal = value;
      if (!isSetLocation) {
        this.locationChange.emit(this.airportInfo);
      }
    } else {
      this.address.Terminal = value;
      if (!isSetLocation) {
        this.addressChanged.emit(this.address);
      }
    }
  }
  onAddStopChange(checked: boolean): void {
    this.addStopChange.emit(checked);
  }
}
